"use client";

import { useEffect } from "react";
import { useLang } from "@/contexts/language-context";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { ArrowRight, CheckCircle2, X } from "lucide-react";
import type { MarketingFeature } from "@/lib/marketing/features-data";

interface FeatureDetailModalProps {
  feature: MarketingFeature | null;
  onClose: () => void;
}

export default function FeatureDetailModal({ feature, onClose }: FeatureDetailModalProps) {
  const { t } = useLang();

  useEffect(() => {
    if (!feature) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [feature, onClose]);

  const isDark = feature?.vertical === "nightlife";
  const Icon = feature?.icon;
  const accent = feature?.accent ?? (isDark ? "#C4B5FD" : "#F58A1F");

  return (
    <AnimatePresence>
      {feature && (
        <>
          {/* Backdrop */}
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md"
            aria-hidden="true"
          />

          {/* Card */}
          <motion.div
            key="modal-card"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feature-modal-headline"
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 pointer-events-none"
          >
            <div
              className={`relative w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-[32px] shadow-2xl pointer-events-auto ${
                isDark
                  ? "bg-[#0F0B1F] border border-white/10 text-white"
                  : "bg-white border border-black/5 text-redy-charcoal"
              }`}
            >
              {/* Accent glow */}
              <div
                className="absolute top-0 right-0 w-[260px] h-[260px] rounded-full blur-[100px] pointer-events-none opacity-30"
                style={{ backgroundColor: accent }}
              />

              {/* Close button */}
              <button
                onClick={onClose}
                aria-label={t("Close", "Cerrar")}
                className={`absolute top-5 right-5 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                  isDark
                    ? "bg-white/10 hover:bg-white/20 text-white"
                    : "bg-black/5 hover:bg-black/10 text-redy-charcoal"
                }`}
              >
                <X className="w-4 h-4" />
              </button>

              <div className="relative p-8 sm:p-10">
                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-[18px] flex items-center justify-center mb-6 ${
                    isDark ? "bg-white/[0.06] border border-white/10" : "bg-[#FAFAFA] border border-black/5"
                  }`}
                >
                  {Icon && <Icon className="w-6 h-6" style={{ color: accent }} />}
                </div>

                {/* Kicker */}
                <div
                  className="text-[11px] font-bold tracking-widest uppercase mb-3"
                  style={{ color: accent }}
                >
                  {t(feature.modal.kicker.en, feature.modal.kicker.es)}
                </div>

                {/* Headline */}
                <h2
                  id="feature-modal-headline"
                  className={`text-2xl sm:text-[32px] font-black leading-[1.1] tracking-tight mb-5 ${
                    isDark ? "text-white" : "text-redy-charcoal"
                  }`}
                >
                  {t(feature.modal.headline.en, feature.modal.headline.es)}
                </h2>

                {/* Body */}
                <p
                  className={`text-[15px] font-medium leading-relaxed mb-7 ${
                    isDark ? "text-white/65" : "text-redy-secondary"
                  }`}
                >
                  {t(feature.modal.body.en, feature.modal.body.es)}
                </p>

                {/* Highlights */}
                <div
                  className={`rounded-[20px] p-5 mb-8 ${
                    isDark ? "bg-white/[0.03] border border-white/10" : "bg-[#FAFAFA] border border-black/5"
                  }`}
                >
                  <ul className="space-y-3">
                    {feature.modal.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2
                          className="w-4 h-4 mt-0.5 shrink-0"
                          style={{ color: accent }}
                        />
                        <span
                          className={`text-[14px] font-medium leading-relaxed ${
                            isDark ? "text-white/85" : "text-redy-charcoal"
                          }`}
                        >
                          {t(h.en, h.es)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    href={`/features/${feature.slug}`}
                    onClick={onClose}
                    className={`group inline-flex items-center justify-center gap-2 text-[14px] font-bold px-7 py-3.5 rounded-full transition-all active:scale-95 ${
                      isDark
                        ? "text-white shadow-lg"
                        : "text-white bg-redy-charcoal hover:bg-black shadow-sm"
                    }`}
                    style={
                      isDark
                        ? { backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }
                        : undefined
                    }
                  >
                    {t("See full details", "Ver detalles completos")}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    href="/signup"
                    onClick={onClose}
                    className={`inline-flex items-center justify-center text-[14px] font-bold px-7 py-3.5 rounded-full transition-all ${
                      isDark
                        ? "border border-white/15 bg-white/5 hover:bg-white/10 text-white"
                        : "border border-black/10 bg-white hover:bg-[#F0EDE8] text-redy-charcoal shadow-sm"
                    }`}
                  >
                    {t("Get started free", "Comenzar gratis")}
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
